/**
 * A drawn stand-in for the backend's captcha image.
 *
 * The live page serves a real challenge from `GET /captcha`, and so does this
 * form — but the mock backend the review build runs against returns an empty
 * PNG (`signup_captcha` is off in MSW), which would leave a blank box exactly
 * where the design needs to be judged. So when no usable image comes back, the
 * form draws one: same size, same read, same refresh affordance.
 *
 * Pure and seeded — no `Math.random()` in here — so the caller controls when a
 * new challenge appears and SSR/hydration can't disagree about it (the seed is
 * set after mount).
 */

/** Ambiguous glyphs (0/O, 1/I/l) left out, as a real captcha alphabet would. */
const ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";

/** Small deterministic PRNG (mulberry32) — good enough for jitter. */
function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export interface DemoCaptcha {
  /** The five characters drawn — surfaced only so a reviewer can type them. */
  code: string;
  /** `data:image/svg+xml` URL, ready for an <img src>. */
  dataUrl: string;
}

/**
 * Draw a 5-character challenge as an SVG data URL. `width`/`height` match the
 * box each variant reserves for it (150×52 is the live image's proportion).
 */
export function makeDemoCaptcha(
  seed: number,
  { width = 150, height = 52 }: { width?: number; height?: number } = {},
): DemoCaptcha {
  const random = rng(seed);
  const chars = Array.from(
    { length: 5 },
    () => ALPHABET[Math.floor(random() * ALPHABET.length)],
  );

  const step = width / (chars.length + 1);
  const glyphs = chars
    .map((char, i) => {
      const x = step * (i + 1) + (random() - 0.5) * 6;
      const y = height / 2 + 9 + (random() - 0.5) * 8;
      const rotate = (random() - 0.5) * 34;
      const size = 25 + random() * 5;
      const opacity = 0.75 + random() * 0.25;
      return `<text x="${x.toFixed(1)}" y="${y.toFixed(1)}" font-family="Georgia,serif" font-size="${size.toFixed(
        1,
      )}" font-weight="700" fill="#f9fafb" fill-opacity="${opacity.toFixed(
        2,
      )}" transform="rotate(${rotate.toFixed(1)} ${x.toFixed(1)} ${y.toFixed(
        1,
      )})">${char}</text>`;
    })
    .join("");

  // Two sweeping strokes plus speckle — the visual noise a captcha carries.
  const strokes = Array.from({ length: 2 }, (_, i) => {
    const y1 = random() * height;
    const y2 = random() * height;
    const cy = random() * height;
    return `<path d="M0 ${y1.toFixed(1)} Q ${(width / 2).toFixed(1)} ${cy.toFixed(
      1,
    )} ${width} ${y2.toFixed(1)}" fill="none" stroke="#0093fd" stroke-opacity="${(
      0.3 +
      i * 0.12
    ).toFixed(2)}" stroke-width="1.5"/>`;
  }).join("");

  const speckle = Array.from({ length: 22 }, () => {
    const x = random() * width;
    const y = random() * height;
    return `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="${(
      0.6 +
      random() * 1.1
    ).toFixed(2)}" fill="#f9fafb" fill-opacity="0.18"/>`;
  }).join("");

  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">` +
    `<rect width="${width}" height="${height}" fill="#1a1f23"/>` +
    speckle +
    glyphs +
    strokes +
    `</svg>`;

  // Percent-encoded rather than raw: `#` in the fills would otherwise start a
  // fragment and truncate the URL.
  return {
    code: chars.join(""),
    dataUrl: `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`,
  };
}
